import type { Choice } from '@pons/contracts';
import type { CoinView } from './model';

export const MAX_BET_BURSTS = 12;
export interface BetSnapshot {
  id: string;
  simulated: boolean;
  at: number;
  total: number;
  up: number;
}
export interface NewBets {
  side: Choice;
  count: number;
}
export interface BetBurst extends NewBets {
  id: number;
  coinId: string;
  amount: number | null;
  inset: number;
  top: number;
  path: string;
  rotation: number;
  ms: number;
  delay: number;
  expiresAt: number;
}

type BetCoin = Pick<
  CoinView,
  | 'id'
  | 'simulated'
  | 'votes'
  | 'votesUp'
  | 'stale'
  | 'choice'
  | 'phase'
  | 'start'
  | 'closesAt'
>;
export function betSnapshot(
  coin: BetCoin,
  now: number,
  enabled: boolean,
): BetSnapshot | null {
  if (
    !enabled ||
    coin.stale ||
    coin.choice ||
    coin.phase !== 'open' ||
    !Number.isFinite(now) ||
    !Number.isFinite(coin.start) ||
    !Number.isFinite(coin.closesAt) ||
    now < coin.start ||
    now >= coin.closesAt ||
    coin.votes === null ||
    coin.votesUp === null ||
    !Number.isSafeInteger(coin.votes) ||
    !Number.isSafeInteger(coin.votesUp) ||
    coin.votes < 0 ||
    coin.votesUp < 0 ||
    coin.votesUp > coin.votes
  )
    return null;
  return {
    id: coin.id,
    simulated: coin.simulated,
    at: now,
    total: coin.votes,
    up: coin.votesUp,
  };
}

// First snapshots, corrections and recovered evidence are not new trades.
// Use actual side counts, never infer them from the rounded percentage.
export function newBets(
  previous: BetSnapshot | null,
  next: BetSnapshot | null,
): NewBets[] {
  if (
    !previous ||
    !next ||
    previous.id !== next.id ||
    previous.simulated !== next.simulated ||
    next.at < previous.at ||
    next.at - previous.at > 2000
  )
    return [];
  const up = next.up - previous.up;
  const down = next.total - next.up - (previous.total - previous.up);
  if (up < 0 || down < 0) return [];
  return [
    ...(down > 0 ? [{ side: 'DIE' as const, count: down }] : []),
    ...(up > 0 ? [{ side: 'RUN' as const, count: up }] : []),
  ];
}

function seedFor(value: string) {
  let seed = 2166136261;
  for (const character of value)
    seed = Math.imul(seed ^ character.charCodeAt(0), 16777619);
  return seed >>> 0;
}

export function createBetBursts(
  snapshot: BetSnapshot,
  changes: NewBets[],
  serial: number,
  now: number,
) {
  const bursts: BetBurst[] = [];
  for (const change of changes) {
    // Aggregate a busy batch without dropping its count or flooding the screen.
    const groups = Math.min(3, change.count);
    for (let i = 0; i < groups; i++) {
      const id = serial + bursts.length;
      const count =
        Math.floor(change.count / groups) + Number(i < change.count % groups);
      const seed = seedFor(snapshot.id + ':' + id + ':' + change.side);
      const direction = change.side === 'DIE' ? -1 : 1;
      // Cycle the fan independently of the amount: a run of similar stakes
      // should not all follow the same arc, and the two buttons fan differently.
      const fan = (id + (direction === -1 ? 0 : 2)) % 5;
      const angle = ([-48, -28, -8, 20, 42][fan] * Math.PI) / 180;
      const dx = direction * (82 + (seed % 43));
      const dy = [-94, -66, -38, 34, 64][fan];
      const ms = 1120 + (seed % 301);
      const delay = i * 65 + (seed % 35);
      bursts.push({
        id,
        coinId: snapshot.id,
        side: change.side,
        count,
        // These are decorative demo stakes, not rewards or inferred live USD.
        amount: snapshot.simulated
          ? [10, 25, 50, 100, 250][seed % 5] * count
          : null,
        // The two outside corners are semicircles with a 32px radius.
        inset: 32 * (1 - Math.cos(angle)),
        top: 32 + 32 * Math.sin(angle),
        path: `path("M 0 0 C ${dx * 0.4} ${dy * 0.12} ${dx * 0.82} ${dy * 0.55} ${dx} ${dy}")`,
        rotation: direction * (4 + (seed % 9)),
        ms,
        delay,
        expiresAt: now + ms + delay,
      });
    }
  }
  return bursts;
}
